import React, { useEffect, useMemo, useState } from "react";

const PROVIDERS = [
  { value: "github", label: "GitHub" },
  { value: "azure_devops", label: "Azure DevOps" },
  { value: "servicenow", label: "ServiceNow" },
  { value: "salesforce", label: "Salesforce" },
  { value: "sap", label: "SAP" },
  { value: "oracle_apps", label: "Oracle Apps" },
  { value: "netsuite", label: "NetSuite" },
];

function safeJsonParse(s, fallback = {}) {
  try {
    return JSON.parse(s || "{}");
  } catch {
    return fallback;
  }
}

function hasAnySecret(secretObj) {
  if (!secretObj || typeof secretObj !== "object") return false;
  return Object.values(secretObj).some((v) => String(v || "").trim() !== "");
}

export default function ConnectionForm({
  initial,
  onSubmit,
  submitting,
  submitLabel = "Save",
  formId = "connection-form",
}) {
  const isEdit = !!initial?.id;

  const [provider, setProvider] = useState(initial?.provider || "servicenow");

  // top-level fields
  const [name, setName] = useState(initial?.name || "");
  const [baseUrl, setBaseUrl] = useState(initial?.base_url || "");
  const [username, setUsername] = useState(initial?.username || "");
  const [authType, setAuthType] = useState(initial?.auth_type || "");

  // settings_json fields
  const initialSettings = useMemo(() => initial?.settings_json || {}, [initial]);
  const [settings, setSettings] = useState(initialSettings);

  // secret fields (never prefilled)
  const [secrets, setSecrets] = useState({});

  // raw JSON editor
  const [settingsRaw, setSettingsRaw] = useState(() =>
    JSON.stringify(initialSettings || {}, null, 2)
  );

  useEffect(() => {
    // keep raw editor in sync on provider change (not on every keystroke)
    setSettingsRaw(JSON.stringify(settings || {}, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const setSetting = (k, v) => setSettings((p) => ({ ...(p || {}), [k]: v }));
  const setSecret = (k, v) => setSecrets((p) => ({ ...(p || {}), [k]: v }));

  const submit = async (e) => {
    e.preventDefault();

    const rawOverrides = safeJsonParse(settingsRaw, {});
    const merged = { ...(settings || {}), ...rawOverrides };
    setSettings(merged);

    const payload = {
      name: name.trim(),
      provider,
      base_url: baseUrl?.trim() || null,
      username: username?.trim() || null,
      auth_type: authType?.trim() || null,
      settings_json: merged,
    };

    const secret_json = secrets || {};
    const includeSecrets = !isEdit || hasAnySecret(secret_json);
    if (includeSecrets) payload.secret_json = secret_json;

    await onSubmit(payload);
  };

  const renderProviderFields = () => {
    const p = String(provider || "").toLowerCase();

    if (p === "github") {
      return (
        <div className="field">
          <label>GitHub Token</label>
          <input
            type="password"
            value={secrets.token || ""}
            onChange={(e) => setSecret("token", e.target.value)}
            placeholder={isEdit ? "Enter new token to update (leave blank to keep existing)" : "token"}
          />
        </div>
      );
    }

    if (p === "azure_devops") {
      return (
        <>
          <div className="grid-2">
            <div className="field">
              <label>Org</label>
              <input
                value={settings.org || ""}
                onChange={(e) => setSetting("org", e.target.value)}
                placeholder="e.g. myorg"
              />
            </div>
            <div className="field">
              <label>PAT</label>
              <input
                type="password"
                value={secrets.pat || ""}
                onChange={(e) => setSecret("pat", e.target.value)}
                placeholder={isEdit ? "Enter new PAT to update (leave blank to keep existing)" : "PAT"}
              />
            </div>
          </div>
          <div className="hint">Azure DevOps uses PAT via Basic auth.</div>
        </>
      );
    }

    if (p === "servicenow") {
      return (
        <>
          <div className="grid-2">
            <div className="field">
              <label>Instance URL (base_url)</label>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://instance.service-now.com"
              />
            </div>
            <div className="field">
              <label>Auth Type</label>
              <select value={authType || "basic"} onChange={(e) => setAuthType(e.target.value)}>
                <option value="basic">basic (username/password)</option>
                <option value="token">token (Bearer)</option>
              </select>
            </div>
          </div>

          {String(authType || "basic") === "token" ? (
            <div className="field">
              <label>Bearer Token</label>
              <input
                type="password"
                value={secrets.token || ""}
                onChange={(e) => setSecret("token", e.target.value)}
                placeholder={isEdit ? "Enter new token to update (leave blank to keep existing)" : "token"}
              />
            </div>
          ) : (
            <div className="grid-2">
              <div className="field">
                <label>Username</label>
                <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" />
              </div>
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={secrets.password || ""}
                  onChange={(e) => setSecret("password", e.target.value)}
                  placeholder={isEdit ? "Enter new password to update (leave blank to keep existing)" : "password"}
                />
              </div>
            </div>
          )}

          <div className="hint">
            Scanner export will pull Script Includes / Business Rules / UI Scripts (based on backend support).
          </div>
        </>
      );
    }

    if (p === "salesforce") {
      return (
        <>
          <div className="grid-2">
            <div className="field">
              <label>Login URL (base_url)</label>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://login.salesforce.com or https://test.salesforce.com"
              />
            </div>
            <div className="field">
              <label>API Version</label>
              <input
                value={settings.api_version || settings.sfdc_api_version || "60.0"}
                onChange={(e) => {
                  setSetting("api_version", e.target.value);
                  setSetting("sfdc_api_version", e.target.value); // compat
                }}
                placeholder="60.0"
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label>Username</label>
              <input
                value={secrets.username || ""}
                onChange={(e) => setSecret("username", e.target.value)}
                placeholder={isEdit ? "Enter new username to update (leave blank to keep existing)" : "user@company.com"}
              />
            </div>
            <div className="field">
              <label>Password</label>
              <input
                type="password"
                value={secrets.password || ""}
                onChange={(e) => setSecret("password", e.target.value)}
                placeholder={isEdit ? "Enter new password to update (leave blank to keep existing)" : "password"}
              />
            </div>
          </div>

          <div className="field">
            <label>Security Token</label>
            <input
              type="password"
              value={secrets.security_token || ""}
              onChange={(e) => setSecret("security_token", e.target.value)}
              placeholder={isEdit ? "Enter new token to update (leave blank to keep existing)" : "security token"}
            />
          </div>

          <div className="hint">
            SOAP login uses <b>password + security token</b> (concatenated). Sandbox must use <b>test.salesforce.com</b>.
          </div>
        </>
      );
    }

    if (p === "sap") {
      return (
        <>
          <div className="grid-2">
            <div className="field">
              <label>SAP Base URL (base_url)</label>
              <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://sap-host:port" />
            </div>
            <div className="field">
              <label>Health Path</label>
              <input
                value={settings.health_path || "/sap/public/ping"}
                onChange={(e) => setSetting("health_path", e.target.value)}
                placeholder="/sap/public/ping"
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label>Username</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" />
            </div>
            <div className="field">
              <label>Password</label>
              <input
                type="password"
                value={secrets.password || ""}
                onChange={(e) => setSecret("password", e.target.value)}
                placeholder={isEdit ? "Enter new password to update (leave blank to keep existing)" : "password"}
              />
            </div>
          </div>

          <div className="field">
            <label>Verify SSL</label>
            <select
              value={String(settings.verify_ssl ?? true)}
              onChange={(e) => setSetting("verify_ssl", e.target.value === "true")}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
        </>
      );
    }

    if (p === "oracle_apps") {
      return (
        <>
          <div className="grid-2">
            <div className="field">
              <label>Oracle Apps Base URL (base_url)</label>
              <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://host" />
            </div>
            <div className="field">
              <label>Test Path</label>
              <input
                value={settings.test_path || "/fscmRestApi/resources/latest/"}
                onChange={(e) => setSetting("test_path", e.target.value)}
                placeholder="/fscmRestApi/resources/latest/"
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label>Username</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" />
            </div>
            <div className="field">
              <label>Password</label>
              <input
                type="password"
                value={secrets.password || ""}
                onChange={(e) => setSecret("password", e.target.value)}
                placeholder={isEdit ? "Enter new password to update (leave blank to keep existing)" : "password"}
              />
            </div>
          </div>

          <div className="field">
            <label>Verify SSL</label>
            <select
              value={String(settings.verify_ssl ?? true)}
              onChange={(e) => setSetting("verify_ssl", e.target.value === "true")}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
        </>
      );
    }

    if (p === "netsuite") {
      return (
        <>
          <div className="muted small">
            NetSuite auth varies (TBA/OAuth). Store account + tokens in secret_json/settings_json as needed.
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Account ID (settings_json.account_id)</label>
              <input
                value={settings.account_id || ""}
                onChange={(e) => setSetting("account_id", e.target.value)}
                placeholder="e.g. 1234567_SB1"
              />
            </div>
            <div className="field">
              <label>Token (secret_json.token)</label>
              <input
                type="password"
                value={secrets.token || ""}
                onChange={(e) => setSecret("token", e.target.value)}
                placeholder={isEdit ? "Enter new token to update (leave blank to keep existing)" : "token"}
              />
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <form id={formId} className="form" onSubmit={submit}>
      <div className="grid-2">
        <div className="field">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SFDC - Prod" />
        </div>

        <div className="field">
          <label>Provider</label>
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>{renderProviderFields()}</div>

      <div className="field" style={{ marginTop: 12 }}>
        <label>Advanced: settings_json</label>
        <textarea
          rows={6}
          value={settingsRaw}
          onChange={(e) => setSettingsRaw(e.target.value)}
          placeholder='{"verify_ssl": true}'
        />
        <div className="hint">Optional. This merges into settings_json used by backend.</div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
        <button className="btn primary" type="submit" disabled={submitting}>
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
